import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Input, Select, Textarea, Skeleton, Modal } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { SubscriptionLockNotice, useSubscriptionLocked } from '../components/SubscriptionLockNotice';
import { createMember, updateMember, getMember, setMemberCoach, setMemberCorporate, getCorporateClients } from '../lib/gymApi';
import { useGymData } from '../data/GymDataProvider';
import { useAsync } from '../lib/useAsync';
import { todayIso } from '../lib/format';
import { isIntlPhone, normalizeIntlPhone } from '../lib/phone';

export function MemberFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  const { coaches, plans } = useGymData();
  const locked = useSubscriptionLocked();

  // When we arrive from another flow (e.g. Corporate → Add member) the caller passes the company to
  // preselect and a `from` path to return to after a success confirmation.
  const navState = (location.state as { corporateId?: string; from?: string } | null) ?? null;

  // Converting a guest into a member: details + the originating pass arrive as query params.
  const [sp] = useSearchParams();
  const guestPassId = sp.get('guestPass') ?? undefined;
  // Converting a lead into a member: mark the lead JOINED on create (settles the pipeline).
  const leadId = sp.get('lead') ?? undefined;

  const [fullName, setFullName] = useState(() => sp.get('name') ?? '');
  const [phone, setPhone] = useState(() => sp.get('phone') ?? '');
  const [waSame, setWaSame] = useState(true); // most members use one number — fast path
  const [waPhone, setWaPhone] = useState('');
  const [email, setEmail] = useState(() => sp.get('email') ?? '');
  const [joinDate, setJoinDate] = useState(todayIso);
  const [notes, setNotes] = useState('');
  const [dob, setDob] = useState('');
  const [coachId, setCoachId] = useState('');
  const [initialCoachId, setInitialCoachId] = useState('');
  const [corporateId, setCorporateId] = useState(() => sp.get('corporate') ?? navState?.corporateId ?? '');
  const [initialCorporateId, setInitialCorporateId] = useState('');
  const { data: companies } = useAsync(getCorporateClients);
  // Success dialog after adding — then return to where we came from (Corporate) or the new member.
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);
  // Changeover from a paper system: record an existing active membership on add.
  const [hasExisting, setHasExisting] = useState(false);
  const [existingPlanId, setExistingPlanId] = useState('');
  const [existingExpiry, setExistingExpiry] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit mode: prefill from the existing record.
  const { data: existing, loading } = useAsync(() => (isEdit ? getMember(id!) : Promise.resolve(null)), [id]);
  useEffect(() => {
    if (!existing) return;
    setFullName(existing.fullName);
    setPhone(existing.phone);
    const same = !!existing.whatsappPhone && existing.whatsappPhone === existing.phone;
    setWaSame(same);
    setWaPhone(same ? '' : (existing.whatsappPhone ?? ''));
    setEmail(existing.email ?? '');
    setNotes(existing.notes ?? '');
    setDob(existing.dateOfBirth ?? '');
    setCoachId(existing.coachId ?? '');
    setInitialCoachId(existing.coachId ?? '');
    setCorporateId(existing.corporateClientId ?? '');
    setInitialCorporateId(existing.corporateClientId ?? '');
  }, [existing]);

  function toggleSame(checked: boolean) {
    setWaSame(checked);
    if (!checked) setWaPhone(phone); // copy the call number over so it's editable, not blank
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!isIntlPhone(phone)) {
      setError('Enter the call number in international format, e.g. +263 77 123 4567.');
      return;
    }
    const rawWa = (waSame ? phone : waPhone).trim();
    if (rawWa && !isIntlPhone(rawWa)) {
      setError('Enter the WhatsApp number in international format, e.g. +263 77 123 4567.');
      return;
    }
    const phoneNorm = normalizeIntlPhone(phone);
    const whatsappPhone = rawWa ? normalizeIntlPhone(rawWa) : ''; // '' clears it on the backend

    if (!isEdit && hasExisting && (!existingPlanId || !existingExpiry)) {
      setError('Pick their current plan and expiry date, or untick the existing-membership box.');
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        await updateMember(id!, {
          fullName: fullName.trim(),
          phone: phoneNorm,
          whatsappPhone,
          email: email.trim(),
          notes: notes.trim(),
          dateOfBirth: dob || undefined,
        });
        // Coach and company are separate assignment endpoints, not profile fields.
        if (coachId !== initialCoachId) {
          await setMemberCoach(id!, coachId || null);
        }
        if (corporateId !== initialCorporateId) {
          await setMemberCorporate(id!, corporateId || null);
        }
        navigate(`/members/${id}`, { replace: true });
      } else {
        const created = await createMember({
          fullName: fullName.trim(),
          phone: phoneNorm,
          whatsappPhone: whatsappPhone || undefined,
          email: email.trim() || undefined,
          joinDate,
          notes: notes.trim() || undefined,
          dateOfBirth: dob || undefined,
          coachId: coachId || undefined,
          corporateClientId: corporateId || undefined,
          initialPlanId: hasExisting && existingPlanId ? existingPlanId : undefined,
          initialExpiry: hasExisting && existingExpiry ? existingExpiry : undefined,
          guestPassId,
          leadId,
        });
        if (navState?.from) {
          // Came from another flow (e.g. Corporate) — confirm, then "Okay" returns there.
          setCreated({ id: created.id, name: created.fullName });
          setSubmitting(false);
        } else {
          navigate(`/members/${created.id}`, { replace: true });
        }
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the member. Please try again.');
      setSubmitting(false);
    }
  }

  function finishSuccess() {
    if (!created) return;
    navigate(navState?.from ?? `/members/${created.id}`, { replace: true });
  }

  const companyName = corporateId ? companies?.find((c) => c.id === corporateId)?.name : undefined;

  // Adding members is blocked while the subscription is lapsed; editing existing ones stays open.
  if (!isEdit && locked) {
    return <SubscriptionLockNotice action="add members" />;
  }

  if (isEdit && loading) {
    return (
      <section className="animate-fade-up">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="mt-4 h-96" />
      </section>
    );
  }

  return (
    <section className="animate-fade-up">
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Back
      </button>

      <PageHeader
        eyebrow={isEdit ? 'Edit member' : 'New member'}
        title={isEdit ? 'Edit member' : 'Add a member'}
        description="A call number is required. WhatsApp is used for reminders; if there’s none, reminders go to email."
      />

      <form onSubmit={onSubmit}>
        <Card className="flex flex-col gap-5 p-5 md:p-6">
          <Input label="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} required autoFocus />

          <Input
            label="Call number"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+263 77 123 4567"
            hint="International format, with country code."
            required
          />

          {/* WhatsApp defaults to the call number; only ask for a separate one when it differs. */}
          <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm font-medium text-ink-soft">
            <input
              type="checkbox"
              checked={waSame}
              onChange={(e) => toggleSame(e.target.checked)}
              className="size-4 rounded border-line accent-ink"
            />
            WhatsApp is the same as the call number
          </label>

          {!waSame && (
            <Input
              label="WhatsApp number"
              type="tel"
              inputMode="tel"
              value={waPhone}
              onChange={(e) => setWaPhone(e.target.value)}
              placeholder="+263 77 123 4567"
              hint="Leave blank if this member has no WhatsApp; reminders will use email."
            />
          )}

          <Input
            label="Email (optional)"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="member@email.com"
            hint="The member signs in to your member portal with this email (a one-time code, no password). Without an email they can still be on your roster, but they won't be able to sign in themselves."
          />

          {!isEdit && (
            <Input label="Join date" type="date" value={joinDate} onChange={(e) => setJoinDate(e.target.value)} required />
          )}

          <Input
            label="Date of birth (optional)"
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            hint="Used for birthday reminders."
          />

          {/* Changeover: capture an already-active membership from the old/paper system. */}
          {!isEdit && plans.length > 0 && (
            <div className="flex flex-col gap-3 rounded-control border border-line bg-surface p-4">
              <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm font-medium text-ink">
                <input
                  type="checkbox"
                  checked={hasExisting}
                  onChange={(e) => setHasExisting(e.target.checked)}
                  className="size-4 rounded border-line accent-ink"
                />
                They already have an active membership
              </label>
              {hasExisting && (
                <>
                  <p className="text-xs text-muted">
                    Moving them over from your old system. Record their current plan and when it
                    expires so they show as active right away. No payment is charged.
                  </p>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Select label="Current plan" value={existingPlanId} onChange={(e) => setExistingPlanId(e.target.value)}>
                      <option value="">— Select a plan —</option>
                      {plans.filter((p) => p.active).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </Select>
                    <Input
                      label="Expires on"
                      type="date"
                      value={existingExpiry}
                      onChange={(e) => setExistingExpiry(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {(coaches.length > 0 || coachId) && (
            <Select label="Coach (optional)" value={coachId} onChange={(e) => setCoachId(e.target.value)}>
              <option value="">— No coach —</option>
              {coaches.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.fullName}
                </option>
              ))}
              {/* Keep a previously-assigned coach selectable even if they've since been un-flagged. */}
              {coachId && !coaches.some((c) => c.id === coachId) && existing?.coachName && (
                <option value={coachId}>{existing.coachName}</option>
              )}
            </Select>
          )}

          {((companies?.length ?? 0) > 0 || corporateId) && (
            <Select
              label="Company (optional)"
              value={corporateId}
              onChange={(e) => setCorporateId(e.target.value)}
              hint="Link this member to a company that sponsors them."
            >
              <option value="">No company</option>
              {(companies ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}

          <Textarea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="e.g. prefers evening sessions" />

          {error && (
            <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2.5 text-sm font-medium text-bad-ink">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              {!submitting && <Check className="size-4" aria-hidden />}
              {isEdit ? 'Save changes' : 'Add member'}
            </Button>
          </div>
        </Card>
      </form>

      {created && (
        <Modal open onClose={finishSuccess}>
          <div className="flex w-full flex-col items-center gap-3 py-2 text-center">
            <span className="grid size-12 place-items-center rounded-full bg-ok-soft text-ok-ink">
              <Check className="size-6" aria-hidden />
            </span>
            <h2 className="font-display text-lg font-semibold text-ink">Member added</h2>
            <p className="text-sm text-muted">
              {created.name} has been added{companyName ? ` to ${companyName}` : ''}.
            </p>
            <Button className="mt-2 w-full" onClick={finishSuccess}>Okay</Button>
          </div>
        </Modal>
      )}
    </section>
  );
}
